import { FabricImage, classRegistry, util } from "fabric";
import { isWebmUrl } from "./invitationMediaUtils";

export const INVITATION_FABRIC_VIDEO_TYPE = "InvitationFabricVideo";

/** Thrown when the canvas already has the maximum number of WebM videos. */
export class MaxInvitationVideosError extends Error {
  constructor() {
    super("Maximum 2 videos per invitation");
    this.name = "MaxInvitationVideosError";
  }
}

let _registered = false;

/**
 * Register the custom video class with Fabric's class registry (idempotent).
 */
export function registerInvitationFabricVideo() {
  if (_registered) return;
  classRegistry.setClass(InvitationFabricVideo, INVITATION_FABRIC_VIDEO_TYPE);
  _registered = true;
}

function loadVideoElementFromUrl(url, options = {}) {
  const { crossOrigin = "anonymous", signal } = options;
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("aborted"));
      return;
    }
    const v = document.createElement("video");
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.preload = "auto";
    if (crossOrigin) v.crossOrigin = crossOrigin;

    const cleanup = () => {
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("error", onError);
      signal?.removeEventListener?.("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      v.src = "";
      reject(new Error("aborted"));
    };

    const onLoaded = () => {
      cleanup();
      resolve(v);
    };

    const onError = () => {
      cleanup();
      reject(new Error(`Could not load video: ${url}`));
    };

    // Use loadedmetadata so videoWidth/videoHeight are set (FabricImage uses
    // naturalWidth/naturalHeight which do not apply to <video>).
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("error", onError);
    if (signal) signal.addEventListener("abort", onAbort, { once: true });

    v.src = url;
    v.load();
  });
}

/**
 * Load a WebM URL into a video element suitable for Fabric (CORS + blob fallback).
 */
export async function loadInvitationFabricVideoFromUrl(url) {
  if (!url || typeof url !== "string") {
    throw new Error("Invalid video URL");
  }
  try {
    return await loadVideoElementFromUrl(url, { crossOrigin: "anonymous" });
  } catch {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) {
      throw new Error(`Could not load video (${res.status})`);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    try {
      return await loadVideoElementFromUrl(blobUrl, { crossOrigin: null });
    } finally {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }
  }
}

function migrateImageToVideoType(o) {
  if (!o || typeof o !== "object") return;
  const t = (o.type || "").toLowerCase();
  const src = typeof o.src === "string" ? o.src : "";
  if (
    (t === "image" || t === "fabricimage") &&
    isWebmUrl(src)
  ) {
    o.type = INVITATION_FABRIC_VIDEO_TYPE;
  }
  if (Array.isArray(o.objects)) {
    o.objects.forEach(migrateImageToVideoType);
  }
}

/**
 * Rewrites legacy Image JSON with WebM src so Fabric does not use loadImage().
 */
export function sanitizeInvitationCanvasJsonForLoad(root) {
  if (!root || typeof root !== "object") return;
  if (Array.isArray(root.objects)) {
    root.objects.forEach(migrateImageToVideoType);
  }
  migrateImageToVideoType(root.backgroundImage);
  migrateImageToVideoType(root.overlayImage);
}

export class InvitationFabricVideo extends FabricImage {
  static type = INVITATION_FABRIC_VIDEO_TYPE;

  static getDefaults() {
    return { ...super.getDefaults(), objectCaching: false };
  }

  /**
   * FabricImage uses naturalWidth/naturalHeight; video elements use videoWidth/videoHeight.
   */
  getOriginalSize() {
    const element = this.getElement();
    if (!element) {
      return { width: 0, height: 0 };
    }
    if (element.tagName === "VIDEO") {
      const w = element.videoWidth || element.width || 1;
      const h = element.videoHeight || element.height || 1;
      return { width: w, height: h };
    }
    return super.getOriginalSize();
  }

  /**
   * Same as FabricImage._renderFill but uses videoWidth/videoHeight for &lt;video&gt;.
   */
  _renderFill(ctx) {
    const elementToDraw = this._element;
    if (!elementToDraw) {
      return;
    }
    const isVideo = elementToDraw.tagName === "VIDEO";
    const elWidth = isVideo
      ? elementToDraw.videoWidth || elementToDraw.width || 1
      : elementToDraw.naturalWidth || elementToDraw.width;
    const elHeight = isVideo
      ? elementToDraw.videoHeight || elementToDraw.height || 1
      : elementToDraw.naturalHeight || elementToDraw.height;

    const scaleX = this._filterScalingX;
    const scaleY = this._filterScalingY;
    const w = this.width;
    const h = this.height;
    const cropX = Math.max(this.cropX, 0);
    const cropY = Math.max(this.cropY, 0);
    const sX = cropX * scaleX;
    const sY = cropY * scaleY;
    const sW = Math.min(w * scaleX, elWidth - sX);
    const sH = Math.min(h * scaleY, elHeight - sY);
    const x = -w / 2;
    const y = -h / 2;
    const maxDestW = Math.min(w, elWidth / scaleX - cropX);
    const maxDestH = Math.min(h, elHeight / scaleY - cropY);
    ctx.drawImage(elementToDraw, sX, sY, sW, sH, x, y, maxDestW, maxDestH);
  }

  /**
   * Same shape as FabricImage.fromObject, but loads a &lt;video&gt; element.
   */
  static fromObject(object, options) {
    const {
      filters: f,
      resizeFilter: rf,
      src,
      crossOrigin,
      type: _type,
      ...rest
    } = object;
    const { enlivenObjects, enlivenObjectEnlivables } = util;
    return Promise.all([
      loadVideoElementFromUrl(src, {
        ...options,
        crossOrigin,
      }),
      f && enlivenObjects(f, options),
      rf ? enlivenObjects([rf], options) : [],
      enlivenObjectEnlivables(rest, options),
    ]).then((_ref2) => {
      const [el, filters, resizeFilterArr, hydratedProps = {}] = _ref2;
      const resizeFilter = resizeFilterArr && resizeFilterArr[0];
      return new this(el, {
        ...rest,
        src,
        filters: filters || [],
        resizeFilter,
        ...hydratedProps,
      });
    });
  }

  tryPlay() {
    const el = this._element || this.getElement?.();
    if (el && el.tagName === "VIDEO") {
      el.play().catch(() => {});
    }
  }

  dispose() {
    const el = this._element || this.getElement?.();
    if (el && el.tagName === "VIDEO") {
      try {
        el.pause();
        el.src = "";
        el.load();
      } catch {
        /* ignore */
      }
    }
    super.dispose();
  }
}

/**
 * Count InvitationFabricVideo instances on the canvas (including nested groups).
 */
export function countInvitationVideosOnCanvas(canvas) {
  if (!canvas) return 0;
  let n = 0;
  const visit = (obj) => {
    if (!obj) return;
    const t = (obj.type || "").toLowerCase();
    if (t === INVITATION_FABRIC_VIDEO_TYPE.toLowerCase()) {
      n += 1;
      return;
    }
    if (typeof obj.forEachObject === "function") {
      obj.forEachObject((child) => visit(child));
    }
  };
  canvas.getObjects().forEach((o) => visit(o));
  visit(canvas.backgroundImage);
  visit(canvas.overlayImage);
  return n;
}

/**
 * Start playback for all video objects (e.g. after loadFromJSON).
 */
export function playInvitationVideosOnCanvas(canvas) {
  if (!canvas) return;
  const visit = (obj) => {
    if (!obj) return;
    if (
      typeof obj.tryPlay === "function" &&
      (obj.type || "").toLowerCase() === INVITATION_FABRIC_VIDEO_TYPE.toLowerCase()
    ) {
      obj.tryPlay();
    }
    if (typeof obj.forEachObject === "function") {
      obj.forEachObject((child) => visit(child));
    }
  };
  canvas.getObjects().forEach((o) => visit(o));
  visit(canvas.backgroundImage);
  visit(canvas.overlayImage);
}
